import { FileSelectArea } from '@/components/file-select-area';
import { LinearLoading } from '@/components/linear-loading';
import { SimpleUpload, SimpleUploadProps } from '@/components/simple-upload';
import { useSnackbar } from '@/contexts/SnackBarContext';
import { useGetAchNoticeStatusQuery } from '@/hooks/suba/useGetAchNoticeStatus.hook';
import { FileProcessingStatus } from '@/models/suba/ach-notice/ach-notice-status-dto.model';
import { AchNoticeService } from '@/services/suba/ach-notice/ach-notice.service';
import { LoadingButton } from '@mui/lab';
import { Alert, AlertTitle, Card, Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import { AxiosError } from 'axios';
import { FC, useCallback, useState } from 'react';
import * as xlsx from 'xlsx';

export const UploadAchNoticeTab: FC = () => {
  // context

  const snackbar = useSnackbar();

  // state

  const [csvFile, setCsvFile] = useState<File | null>();

  // api

  const getAchNoticeStatusQuery = useGetAchNoticeStatusQuery();

  const uploadAchNotice = useMutation(() => AchNoticeService.upload(csvFile), {
    onError: (err: AxiosError) => {
      snackbar.showSnackbar({
        message: `File failed to upload with error: ${err.response?.data ?? err.message}`,
        severity: 'error'
      });
    },
    onSuccess: () => {
      snackbar.showSnackbar({
        message: 'File uploaded successfully'
      });
      getAchNoticeStatusQuery.refetch();
    }
  });

  // callbacks

  const handleUploadClick = useCallback(async () => {
    uploadAchNotice.mutateAsync();
  }, []);

  const handleUploadSelect = useCallback<SimpleUploadProps['onSelect']>(
    async (files: File[]) => {
      if (!files.length) return;

      const file = files[0];

      if (file.type === 'text/csv') {
        return setCsvFile(file);
      }

      const { Sheets, SheetNames } = xlsx.read(await file.arrayBuffer());

      setCsvFile(
        new File(
          [
            xlsx.utils.sheet_to_csv(Sheets[SheetNames[0]], {
              blankrows: false
            })
          ],
          file.name
        )
      );
    },
    []
  );

  if (getAchNoticeStatusQuery.isInitialLoading) {
    return <LinearLoading />;
  }

  return (
    <Stack spacing={2}>
      {[
        FileProcessingStatus.QUEUED,
        FileProcessingStatus.PROCESSING,
        FileProcessingStatus.PROCESSED
      ].includes(getAchNoticeStatusQuery.data?.status) ? (
        <Alert data-testid='alert' severity='warning'>
          <AlertTitle data-testid='alert-title'>
            ACH Notice file already uploaded today
          </AlertTitle>
          Another file may be uploaded tomorrow.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {getAchNoticeStatusQuery.data?.status ===
            FileProcessingStatus.FAILED && (
            <Alert data-testid='alert' severity='error'>
              <AlertTitle data-testid='alert-title'>
                Sorry, something went wrong with today&apos;s file upload
              </AlertTitle>
              Please double-check it&apos;s contents and try uploading again.
            </Alert>
          )}
          <Card elevation={0} variant='outlined'>
            <SimpleUpload
              accept={{
                'application/vnd.ms-excel': ['.xlsx'],
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                  ['.xls'],
                'text/csv': ['.csv']
              }}
              fileInputTestId='file-input'
              onSelect={handleUploadSelect}
              selectRawFiles>
              <FileSelectArea filename={csvFile?.name} />
            </SimpleUpload>
          </Card>
          <LoadingButton
            component='label'
            data-testid='upload-button'
            disabled={
              getAchNoticeStatusQuery.isLoading ||
              getAchNoticeStatusQuery.data?.status === 'QUEUED'
            }
            loading={uploadAchNotice.isLoading}
            onClick={handleUploadClick}
            variant='outlined'>
            Upload
          </LoadingButton>
        </Stack>
      )}
    </Stack>
  );
};

UploadAchNoticeTab.displayName = 'UploadAchNoticeTab';
